import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../../lib/errors';
import { readNextPath, withNext } from '../../lib/nextPath';
import { AuthCard } from './AuthCard';
import { AuthAlert, AuthField, AuthSubmit } from './AuthField';
import { LegalLinks } from './LegalLinks';
import { useAuth } from './useAuth';

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectTo = readNextPath(location);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await signIn(email, password);
      void navigate(redirectTo, { replace: true });
    } catch (caught) {
      setError(getErrorMessage(caught, 'Sign-in failed. Check the email and password.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthCard
      tab="login"
      title="Welcome back"
      description="Sign in to pick up where you left off."
      footer={
        <>
          New to Autopostmate? <Link to={withNext('/signup', redirectTo)}>Start a free trial</Link>
        </>
      }
    >
      <form className="auth-form" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <AuthField
          label="Email"
          type="email"
          name="email"
          placeholder="you@company.com"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <AuthField
          label="Password"
          type="password"
          name="password"
          placeholder="Your password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          trailing={<Link to="/reset-password">Forgot password?</Link>}
        />

        {error ? <AuthAlert message={error} /> : null}

        <AuthSubmit label="Sign in" isBusy={isSubmitting} />

        <LegalLinks action="signing in" />
      </form>
    </AuthCard>
  );
}
